import { BaseButton } from '@/components/common/base-button';
import { TriangleDownIcon, TriangleUpIcon } from '@/components/icons/home';
import { useConfigStore } from '@/stores/configStore';
import {
    EXCHANGE_RATE_AUTO_ROTATE_MS,
    formatExchangeRateValue,
    getCurrencySymbol,
    getExchangeRateSpokenLabel,
    sortExchangeRates,
} from '@/utils/exchange-rate';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

export const ExchangeRateWidget = () => {
    const { t } = useTranslation();
    const exchangeRates = useConfigStore((state) => state.config.server?.exchangeRates);
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const rates = useMemo(() => sortExchangeRates(exchangeRates), [exchangeRates]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [rotationKey, setRotationKey] = useState(0);
    const iconColor = contrast ? '#222222' : 'white';

    const rateCount = rates.length;
    const currentRate = rates[currentIndex];

    useEffect(() => {
        setCurrentIndex(0);
    }, [rateCount]);

    useEffect(() => {
        if (rateCount <= 1) {
            return;
        }

        const timerId = window.setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % rateCount);
        }, EXCHANGE_RATE_AUTO_ROTATE_MS);

        return () => {
            window.clearInterval(timerId);
        };
    }, [rateCount, rotationKey]);

    if (!currentRate) {
        return null;
    }

    const goToPrev = () => {
        setCurrentIndex((prev) => (prev - 1 + rateCount) % rateCount);
        setRotationKey((key) => key + 1);
    };

    const goToNext = () => {
        setCurrentIndex((prev) => (prev + 1) % rateCount);
        setRotationKey((key) => key + 1);
    };

    const symbol = getCurrencySymbol(currentRate.currencyCode);
    const rateValue = formatExchangeRateValue(currentRate.exchangeRate);
    const spokenLabel = getExchangeRateSpokenLabel(currentRate, t);
    const prevLabel = t('home.exchange_rate.tts.prev');
    const nextLabel = t('home.exchange_rate.tts.next');

    return (
        // One region (3-1/3-2/3-3): ArrowUp/Down lands on first focusable (.content)
        <div className={styles.widget} role="group" aria-label={t('home.exchange_rate.label')} data-region-nav data-region-order={2}>
            {/* TTS via global useInteractiveElementsWithTTS (focusin → speakElement). Avoid onFocus speak() — doubles with the global handler. */}
            <div className={styles.content} tabIndex={3} data-interactive role="text" aria-label={spokenLabel}>
                <span className={styles.label} aria-hidden="true">
                    {t('home.exchange_rate.label')}
                </span>
                <span className={styles.rate} aria-hidden="true">
                    <span className={styles.rateFixed}>$1 =</span>
                    <span className={styles.rateValue}>
                        <span>{symbol}</span>
                        <span className={styles.rateAsk}>{rateValue}</span>
                    </span>
                </span>
            </div>
            <div className={styles.controls}>
                {/* Full TTS phrase in aria-label; omit data-role to avoid "…버튼, 버튼" duplication */}
                <BaseButton type="button" className={styles.navButton} tabIndex={4} onClick={goToPrev} data-playsound="ting" aria-label={prevLabel}>
                    <TriangleUpIcon color={iconColor} />
                </BaseButton>
                <BaseButton type="button" className={styles.navButton} tabIndex={5} onClick={goToNext} data-playsound="ting" aria-label={nextLabel}>
                    <TriangleDownIcon color={iconColor} />
                </BaseButton>
            </div>
        </div>
    );
};
